import type { ContentRegistry } from '../content/registry';
import type { ItemDef, ItemSlot, UpgradeDef } from '../content/types';
import type { CloudBackend, LeaderboardKind } from '../meta/backend';
import { dailyCountdown, dailyKey } from '../meta/backend';
import { allocateSkill, assignPet, buyPet, buyUnlock, canAcquirePet, characterLevelProgress, countUnlocks, equip, equippedItems, petAcquisitionLabel, petLevel, respecSkills, skillPointsForLevel, spentSkillPoints, selectPet, type RunResult } from '../meta/profile';
import type { ColorblindMode, ControlsHudMode, SaveData, SettingsData } from '../meta/save';
import type { Sim } from '../sim/sim';
import { clear, fmtTime, h, RARITY_COLOR } from './dom';
import { GAME_VERSION } from '../version';
import { upgradeIcon } from './icons';
import type { Stats } from '../sim/stats';
import { applyEraTheme, eraThemeFor } from './theme';
import { availableStartEras, isEraDiscovered, normalizeRunSetup, type MenuRunSetup } from './menuModel';
import { CHARACTERS, characterDef, characterUnlockLabel, type CharacterId } from '../content/characters';
import { ACHIEVEMENTS, ACHIEVEMENT_CATEGORY_LABELS, achievementRewardLabel, type AchievementCategory } from '../content/achievements';
import { PETS, PET_ASSIGNMENT_STATIONS, PET_BY_ID } from '../content/pets';
import { CHALLENGES } from '../content/challenges';
import { canAllocateSkill, SKILL_TREES, type SkillBranch } from '../content/skills';
import type { CharacterLibrary } from '../render/characters';
import { CharacterPreview } from './characterPreview';
import { ItemPreview } from './itemPreview';
import type { ModelLibrary } from '../render/models';
import { activeDevice, currentInputPrompts, glyphsFor } from './inputPrompts';
import { actionPrompt, glyphEl, livePrompt } from './prompts';
import { controlsPanel, type ControlsActions } from './controlsPanel';
import { LOCALES, tr, type Locale } from './i18n';
import type { ObservatoryStationId } from '../house/observatory';
import { HOUSE_MODULES } from '../house/modules';
import { paradoxDef, paradoxLabel } from '../content/paradox';
import { portalArtStyle } from './portalArt';

export interface RunSetupOptions {
  startEra: number;
  paradox: number;
  players: number;
  daily: false;
}

/**
 * Screens are plain functions that build a DOM subtree and return it. The Game
 * mounts one at a time into #ui. Keep them dumb: they call back into Game.
 */
export interface MenuActions {
  startRun(opts: { startEra: number; paradox: number; players: number; daily: boolean }): void;
  resumeRun(): void;
  watchReplay(): void;
  hasSavedRun: boolean;
  hasReplay: boolean;
  cloudStatus: string;
  openHub(tab?: HubTab): void;
  openLeaderboards(): void;
  openChallenges(): void;
  openOnline(): void;
  updateRunSetup(setup: MenuRunSetup): void;
  setQuality(q: 'low' | 'med' | 'high'): void;
  setMusicVolume(v: number): void;
  setSfxVolume(v: number): void;
  setCameraDistance(v: number): void;
  setColorblindMode(mode: ColorblindMode): void;
  setControlsHud(mode: ControlsHudMode): void;
  setLocale(locale: Locale): void;
  toggle(key: 'screenShake' | 'showDamageNumbers' | 'highContrastProjectiles' | 'reducedFlash', on: boolean): void;
  controls: ControlsActions;
  signIn(kind: 'google' | 'anon'): void;
  signOut(): void;
  syncCloud(): void;
}

export function mainMenu(save: SaveData, reg: ContentRegistry, backend: CloudBackend, a: MenuActions): HTMLElement {
  const t = (key: Parameters<typeof tr>[1]) => tr(save.settings.locale, key);
  const themeEra = eraThemeFor(save, reg);
  const selectedCharacter = characterDef(save.selectedCharacter);
  applyEraTheme(themeEra);
  const setup = normalizeRunSetup(save, reg);
  let { startEra, paradox, players } = setup;
  const persistSetup = () => a.updateRunSetup({ startEra, paradox, players });
  const hex = (n: number) => `#${n.toString(16).padStart(6, '0')}`;
  const startChoices = availableStartEras(save, reg);
  const cycle = (values: number[], current: number) => values[(Math.max(0, values.indexOf(current)) + 1) % values.length];

  const user = backend.currentUser();
  const cloudRow = backend.kind === 'firebase'
    ? user
      ? h('div', { class: 'col' }, h('div', { class: 'row' }, h('span', { class: 'sub' }, `Signed in as ${user.name}`), h('button', { class: 'small', onClick: () => a.syncCloud() }, 'Sync now'), h('button', { class: 'small', onClick: () => a.signOut() }, 'Sign out')), h('div', { class: 'sub' }, a.cloudStatus), h('div', { class: 'sub' }, 'Conflicts merge unlocks, achievements, anchors and best stats; the newest device keeps preferences and loadout.'))
      : h('div', { class: 'col' }, h('div', { class: 'row' }, h('button', { class: 'small', onClick: () => a.signIn('google') }, 'Sign in with Google'), h('button', { class: 'small', onClick: () => a.signIn('anon') }, 'Play as guest (cloud)')), a.cloudStatus && a.cloudStatus !== 'Not signed in' ? h('div', { class: 'sub', style: 'color:var(--gold)' }, a.cloudStatus) : null)
    : h('div', { class: 'sub' }, 'Offline mode — local saves only. Configure the cloud backend to enable cloud saves & leaderboards.');
  const settingsContent = h(
    'div', { class: 'menu-sheet-content' },
    h('div', { class: 'menu-sheet-heading' }, h('div', null, h('h2', null, 'Settings'), h('div', { class: 'sub' }, 'Tune the experience. Changes save immediately.'))),
    h(
      'div',
      { class: 'menu-setting-row' },
      h('span', { class: 'sub' }, 'Quality'),
      ...(['low', 'med', 'high'] as const).map((q) => h('button', { class: save.settings.quality === q ? 'small primary' : 'small', onClick: (e) => { a.setQuality(q); (e.currentTarget as HTMLElement).closest('.menu-screen')?.setAttribute('data-quality', q); (e.currentTarget as HTMLElement).parentElement?.querySelectorAll('button').forEach((b) => b.classList.remove('primary')); (e.currentTarget as HTMLElement).classList.add('primary'); } }, q)),
    ),
    h(
      'div',
      { class: 'menu-setting-row' },
      h('span', { class: 'sub' }, 'Music'),
      h('input', { type: 'range', min: 0, max: 1, step: 0.05, value: save.settings.musicVolume, onInput: (e) => a.setMusicVolume(Number((e.target as HTMLInputElement).value)) }),
      h('span', { class: 'sub' }, 'SFX'),
      h('input', { type: 'range', min: 0, max: 1, step: 0.05, value: save.settings.sfxVolume, onInput: (e) => a.setSfxVolume(Number((e.target as HTMLInputElement).value)) }),
    ),
    h(
      'label',
      { class: 'menu-setting-row' },
      h('span', { class: 'sub' }, 'Camera distance'),
      h('input', { type: 'range', min: 26, max: 44, step: 1, value: save.settings.cameraDistance, onInput: (e) => { const input = e.target as HTMLInputElement; a.setCameraDistance(Number(input.value)); const value = input.nextElementSibling; if (value) value.textContent = input.value; } }),
      h('span', { class: 'sub' }, String(Math.round(save.settings.cameraDistance))),
    ),
    h(
      'div',
      { class: 'col menu-setting-checks' },
      h('label', { class: 'row', style: 'gap:6px;cursor:pointer' }, h('input', { type: 'checkbox', checked: save.settings.screenShake, onChange: (e) => a.toggle('screenShake', (e.target as HTMLInputElement).checked) }), h('span', { class: 'sub' }, 'Screen shake')),
      h('label', { class: 'row', style: 'gap:6px;cursor:pointer' }, h('input', { type: 'checkbox', checked: save.settings.showDamageNumbers, onChange: (e) => a.toggle('showDamageNumbers', (e.target as HTMLInputElement).checked) }), h('span', { class: 'sub' }, 'Damage numbers')),
      h('label', { class: 'row', style: 'gap:6px;cursor:pointer' }, h('input', { type: 'checkbox', checked: save.settings.highContrastProjectiles, onChange: (e) => a.toggle('highContrastProjectiles', (e.target as HTMLInputElement).checked) }), h('span', { class: 'sub' }, 'High-contrast projectiles')),
      h('label', { class: 'row', style: 'gap:6px;cursor:pointer' }, h('input', { type: 'checkbox', checked: save.settings.reducedFlash, onChange: (e) => a.toggle('reducedFlash', (e.target as HTMLInputElement).checked) }), h('span', { class: 'sub' }, 'Reduced flash')),
    ),
    h('div', { class: 'menu-setting-row' }, h('span', { class: 'sub' }, 'Color vision'), ...(['none', 'protanopia', 'deuteranopia', 'tritanopia'] as const).map((mode) => h('button', { class: save.settings.colorblindMode === mode ? 'small primary' : 'small', onClick: () => a.setColorblindMode(mode) }, mode === 'none' ? 'Default' : mode))),
    h('div', { class: 'menu-setting-row' }, h('span', { class: 'sub' }, 'Controls HUD'), h('select', { onChange: (e) => a.setControlsHud((e.target as HTMLSelectElement).value as ControlsHudMode) }, h('option', { value: 'always', selected: save.settings.controlsHud === 'always' }, 'Always'), h('option', { value: 'firstRuns', selected: save.settings.controlsHud === 'firstRuns' }, 'First 3 runs'), h('option', { value: 'never', selected: save.settings.controlsHud === 'never' }, 'Never'))),
    h('label', { class: 'menu-setting-row' }, h('span', { class: 'sub' }, t('settings.language')), h('select', { onChange: (e) => a.setLocale((e.target as HTMLSelectElement).value as Locale) }, ...LOCALES.map((locale) => h('option', { value: locale, selected: save.settings.locale === locale }, locale === 'en' ? 'English' : 'Español')))),
    h('div', { class: 'menu-setting-row' }, h('span', { class: 'sub' }, 'Controls'), h('button', { class: 'small', onClick: () => openSheet(controlsContent()) }, 'Customize controls')),
  );
  const controlsContent = () => h('div', { class: 'menu-sheet-content' },
    h('div', { class: 'menu-sheet-heading' }, h('div', null, h('h2', null, 'Controls'), h('div', { class: 'sub' }, 'Pick a slot, then press the key or button to use. Changes save immediately.'))),
    controlsPanel(a.controls),
    h('button', { class: 'small', onClick: () => openSheet(settingsContent) }, '← Back to settings'),
  );

  const accountContent = h('div', { class: 'menu-sheet-content' }, h('div', { class: 'menu-sheet-heading' }, h('div', null, h('h2', null, 'Account'), h('div', { class: 'sub' }, 'Cloud saves and leaderboard identity.'))), cloudRow);
  const today = dailyKey();
  const dailyAttempted = save.daily[today] !== undefined;
  const countdownNodes: HTMLElement[] = [];
  const dailyButton = (mobile = false) => {
    if (mobile) return h('button', { onClick: () => a.startRun({ startEra: 0, paradox: 0, players: 1, daily: true }) }, h('span', null, dailyAttempted ? '✓' : '◷'), h('small', null, dailyAttempted ? 'Practice' : 'Daily'));
    const countdown = h('small', { class: 'daily-countdown' }, dailyCountdown());
    countdownNodes.push(countdown);
    return h('button', { class: dailyAttempted ? 'daily-used' : '', onClick: () => a.startRun({ startEra: 0, paradox: 0, players: 1, daily: true }) }, `Daily ${dailyAttempted ? 'Practice' : 'Run'} · `, countdown);
  };
  const sheet = h('div', { class: 'menu-sheet', 'aria-hidden': 'true' });
  const closeSheet = () => { sheet.classList.remove('open'); sheet.setAttribute('aria-hidden', 'true'); clear(sheet); };
  const openSheet = (content: HTMLElement) => {
    clear(sheet);
    sheet.append(h('button', { class: 'menu-sheet-backdrop', 'aria-label': 'Close', onClick: closeSheet }), h('section', { class: 'menu-sheet-panel', role: 'dialog', 'aria-modal': 'true' }, h('button', { class: 'menu-sheet-close', 'aria-label': 'Close', onClick: closeSheet }, '×'), content));
    sheet.classList.add('open');
    sheet.setAttribute('aria-hidden', 'false');
  };

  const startValue = h('strong', null);
  const paradoxValue = h('strong', null);
  const playersValue = h('strong', null);
  const refreshSetup = (saveSelection = true) => {
    startValue.textContent = startEra === 0 ? 'Full timeline' : reg.eras[startEra]?.name ?? 'Full timeline';
    paradoxValue.textContent = paradoxLabel(paradox);
    paradoxValue.title = paradoxDef(paradox).description;
    playersValue.textContent = players === 1 ? 'Solo' : `${players} players`;
    if (saveSelection) persistSetup();
  };

  const showcase = h('div', { class: 'menu-portal-showcase', 'aria-label': 'Discovered era portals' });
  const timeline = h('div', { class: 'menu-era-rail', 'aria-label': 'Era timeline' });
  reg.eras.forEach((era) => {
    const discovered = isEraDiscovered(save, era);
    const active = discovered && era.index === themeEra.index;
    const artStyle = portalArtStyle(era.index);
    if (era.implemented) {
      showcase.appendChild(h(
        'button',
        {
          class: `menu-portal-card${discovered ? ' discovered' : ' locked'}${active ? ' active' : ''}`,
          style: discovered ? `${artStyle};--era-color:${hex(era.palette.accent)}` : '',
          disabled: !discovered,
          'aria-label': discovered ? `${era.name} portal` : `Undiscovered era portal`,
          onClick: () => a.openHub(),
        },
        h('span', { class: 'menu-portal-art' }, discovered ? null : h('b', null, '?')),
        h('span', { class: 'menu-portal-label' }, discovered ? era.name : '???'),
      ));
    }
    timeline.appendChild(h(
      'button',
      {
        class: `menu-era${discovered ? ' discovered' : ' locked'}${active ? ' active' : ''}`,
        style: discovered ? `${artStyle};--era-color:${hex(era.palette.accent)};--era-ground:${hex(era.palette.ground)}` : '',
        disabled: !discovered,
        'aria-label': discovered ? `${era.name}, ${era.subtitle}` : `Era ${era.index + 1}, undiscovered`,
        onClick: () => a.openHub(),
      },
      h('span', { class: 'menu-era-orb' }, h('span', { class: 'menu-era-core' }, discovered ? String(era.index + 1).padStart(2, '0') : '?')),
      h('span', { class: 'menu-era-copy' }, h('strong', null, discovered ? era.name : '???'), h('small', null, discovered ? era.subtitle : 'Undiscovered')),
    ));
  });
  refreshSetup(false);

  const root = h(
    'div',
    { class: 'screen menu-screen', 'data-quality': save.settings.quality },
    h(
      'div',
      { class: 'menu-cinematic', 'aria-hidden': 'true' },
      h('div', { class: 'menu-backdrop' }),
      h('div', { class: 'menu-stars' }),
      h('div', { class: 'menu-dust menu-dust-far' }),
      h('div', { class: 'menu-dust menu-dust-near' }),
      h('div', { class: 'menu-rift' }, h('i'), h('i'), h('i')),
      h('div', { class: 'menu-debris' }, ...Array.from({ length: 9 }, () => h('i'))),
      h('div', { class: 'menu-horizon' }),
    ),
    h(
      'header',
      { class: 'menu-nav' },
      h('button', { class: 'menu-wordmark', onClick: () => a.openHub(), 'aria-label': 'Open The Timeline' }, 'CHRONOFALL'),
      h('nav', { class: 'menu-links', 'aria-label': 'Main menu' },
        dailyButton(),
        h('button', { onClick: () => a.openHub() }, t('menu.timeline')),
        h('button', { onClick: () => a.openHub('observatory') }, 'Observatory'),
        h('button', { onClick: () => a.openHub('characters') }, t('menu.characterInventory')),
        h('button', { onClick: () => a.openLeaderboards() }, t('menu.leaderboards')),
        h('button', { onClick: () => a.openChallenges() }, t('menu.challenges')),
        h('button', { onClick: () => a.openOnline() }, t('menu.online')),
      ),
      h('div', { class: 'menu-tools' },
        h('button', { class: 'menu-tool', 'aria-label': t('menu.settings'), title: t('menu.settings'), onClick: () => openSheet(settingsContent) }, h('span', { 'aria-hidden': 'true' }, '⚙'), h('em', null, t('menu.settings'))),
        h('button', { class: 'menu-tool', 'aria-label': t('menu.account'), title: t('menu.account'), onClick: () => openSheet(accountContent) }, h('span', { 'aria-hidden': 'true' }, '◉'), h('em', null, t('menu.account'))),
      ),
    ),
    h(
      'main',
      { class: 'menu-stage' },
      h('section', { class: 'menu-hero' },
        h('div', { class: 'menu-eyebrow' }, h('span'), `${themeEra.name} · ${themeEra.subtitle}`),
        h('h1', null, 'Chronofall'),
        h('p', null, t('menu.tagline'), h('br'), t('menu.fall')),
      ),
      h('section', { class: 'menu-showcase-wrap' }, showcase),
      h('section', { class: 'menu-run' },
        a.hasSavedRun ? h('button', { class: 'menu-begin menu-continue', 'data-default-focus': true, onClick: () => a.resumeRun() }, h('span', null, t('menu.continue')), h('small', null, 'Resume from autosave'), h('b', null, '↻')) : null,
        h('button', { class: 'menu-begin', 'data-default-focus': !a.hasSavedRun, onClick: () => a.startRun({ startEra, paradox, players, daily: false }) }, h('span', null, t('menu.begin')), h('small', null, `${selectedCharacter.name} · ${selectedCharacter.ability.name}`), h('b', { 'aria-hidden': 'true' }, '→')),
        h('button', { class: 'small menu-character-inventory', onClick: () => a.openHub('characters') }, `Character / Inventory · ${selectedCharacter.name}`),
        h('button', { class: 'small menu-character-inventory', onClick: () => a.openHub('observatory') }, 'Enter The Observatory · House & Yard'),
        a.hasReplay ? h('button', { class: 'small menu-replay', onClick: () => a.watchReplay() }, '▷ Watch Last Run') : null,
        h('div', { class: 'menu-run-setup', 'aria-label': 'Remembered run settings' },
          h('button', { onClick: () => { startEra = cycle(startChoices, startEra); refreshSetup(); } }, h('small', null, 'Starting point'), startValue, h('span', null, '↻')),
          h('button', { onClick: () => { paradox = (paradox + 1) % (save.paradoxUnlocked + 1); refreshSetup(); } }, h('small', null, 'Difficulty'), paradoxValue, h('span', null, '↻')),
          h('button', { onClick: () => { players = players % 4 + 1; refreshSetup(); } }, h('small', null, 'Party'), playersValue, h('span', null, '↻')),
        ),
      ),
      h('section', { class: 'menu-timeline' },
        h('div', { class: 'menu-section-title' }, h('span', null, 'Your journey through time'), h('button', { onClick: () => a.openHub() }, `Open timeline · ◆ ${save.chronoshards}`)),
        timeline,
      ),
    ),
    h('footer', { class: 'menu-footer' },
      h('div', { class: 'menu-controls sub' }, actionPrompt('move', 'Move'), actionPrompt('ability', 'Ability'), actionPrompt('pause', 'Pause'), livePrompt(() => zoomPromptParts(), { class: 'live-prompt action-prompt' })),
    ),
    h('nav', { class: 'menu-mobile-nav', 'aria-label': 'Quick links' },
      dailyButton(true),
      h('button', { onClick: () => a.openHub('characters') }, h('span', null, '◇'), h('small', null, 'Character')),
      h('button', { onClick: () => a.openLeaderboards() }, h('span', null, '≡'), h('small', null, 'Ranks')),
      h('button', { onClick: () => a.openChallenges() }, h('span', null, '✦'), h('small', null, 'Trials')),
      h('button', { onClick: () => a.openOnline() }, h('span', null, '⇄'), h('small', null, 'Online')),
      h('button', { onClick: () => openSheet(settingsContent) }, h('span', null, '⚙'), h('small', null, 'Settings')),
    ),
    sheet,
  );
  const countdownTimer = window.setInterval(() => {
    if (!root.isConnected) return window.clearInterval(countdownTimer);
    for (const node of countdownNodes) node.textContent = dailyCountdown();
  }, 1000);
  return root;
}

export function challengeScreen(save: SaveData, onStart: (id: string) => void, onBack: () => void): HTMLElement {
  return h('div', { class: 'screen' }, h('div', { class: 'panel col', style: 'min-width:min(92vw,720px)' },
    h('h2', null, 'Challenge Seeds'),
    h('div', { class: 'sub' }, 'Fixed seeds and loadouts. Profile upgrades, equipment, and pets are disabled.'),
    h('div', { class: 'grid' }, ...CHALLENGES.map((challenge) => {
      const best = save.challenges[challenge.id] ?? 0;
      const complete = best >= challenge.erasToClear;
      return h('div', { class: 'card' + (complete ? ' selected' : '') },
        h('div', { class: 'rarity' }, complete ? 'Complete' : `Reward · ${challenge.rewardShards} shards`),
        h('div', { class: 'name' }, challenge.name),
        h('div', { class: 'desc' }, challenge.description),
        h('div', { class: 'challenge-loadout' }, h('span', null, characterDef(challenge.character).name), h('span', null, challenge.weapon.replace(/^w_/, '').replaceAll('_', ' ')), h('span', null, paradoxLabel(challenge.paradox))),
        h('div', { class: 'sub' }, `Goal · Clear ${challenge.erasToClear} era${challenge.erasToClear === 1 ? '' : 's'}${challenge.requirement ? ` · ${challenge.requirement.label}` : ''}`),
        h('div', { class: 'sub' }, `Seed ${challenge.seed}`),
        h('button', { class: 'small primary', onClick: () => onStart(challenge.id) }, complete ? 'Run again' : 'Begin challenge'));
    })),
    h('button', { class: 'primary', onClick: onBack }, 'Back')));
}

function characterCarousel(save: SaveData, characters: CharacterLibrary, onChanged: () => void, compact = false): HTMLElement {
  let index = Math.max(0, CHARACTERS.findIndex((character) => character.id === save.selectedCharacter));
  const canvas = h('canvas', { class: 'character-preview-canvas', 'aria-label': 'Interactive 3D character preview' });
  const preview = new CharacterPreview(canvas, characters);
  const eyebrow = h('div', { class: 'character-eyebrow' });
  const name = h('div', { class: 'character-name' });
  const tagline = h('div', { class: 'character-tagline' });
  const progression = h('div', { class: 'character-progression' });
  const abilityName = h('strong');
  const abilityDescription = h('span');
  const cooldown = h('span', { class: 'character-cooldown' });
  const passiveName = h('strong');
  const passiveDescription = h('span');
  const lockCopy = h('div', { class: 'character-lock-copy' });
  const stage = h('div', { class: 'character-stage' },
    h('div', { class: 'character-stage-glow' }),
    canvas,
    h('div', { class: 'character-stage-scan' }),
    h('div', { class: 'character-lock-mark', 'aria-hidden': 'true' }, '◇'),
    h('div', { class: 'character-drag-hint' }, 'Drag to rotate'),
  );
  const select = h('button', { class: 'primary character-confirm', 'data-default-focus': !compact }, 'Select Timewalker');
  const previous = h('button', { class: 'character-arrow character-arrow-prev', 'aria-label': 'Previous character' }, '‹');
  const next = h('button', { class: 'character-arrow character-arrow-next', 'aria-label': 'Next character' }, '›');
  const dots = h('div', { class: 'character-roster', role: 'tablist', 'aria-label': 'Timewalker roster' });
  const dotButtons = CHARACTERS.map((character, characterIndex) => {
    const unlocked = save.unlockedCharacters.includes(character.id);
    const button = h('button', {
      class: 'character-roster-dot' + (unlocked ? '' : ' locked'),
      role: 'tab',
      'aria-label': `${character.name}${unlocked ? '' : ', locked'}`,
      onClick: () => { index = characterIndex; renderCharacter(); },
    }, h('span', null, character.name.slice(0, 1)), h('small', null, character.name));
    dots.appendChild(button);
    return button;
  });
  const neighboringName = (offset: number) => CHARACTERS[(index + offset + CHARACTERS.length) % CHARACTERS.length].name;
  const previousLabel = h('span', { class: 'character-neighbor character-neighbor-prev' });
  const nextLabel = h('span', { class: 'character-neighbor character-neighbor-next' });

  const renderCharacter = () => {
    const character = CHARACTERS[index];
    const unlocked = save.unlockedCharacters.includes(character.id);
    const selected = save.selectedCharacter === character.id;
    preview.setCharacter(character.id);
    stage.classList.toggle('locked', !unlocked);
    eyebrow.textContent = selected ? 'Current timewalker' : unlocked ? `Timewalker ${index + 1} / ${CHARACTERS.length}` : 'Timeline encrypted';
    name.textContent = character.name;
    tagline.textContent = character.tagline;
    const progress = characterLevelProgress(save.characters[character.id].xp);
    const unspent = skillPointsForLevel(progress.level) - spentSkillPoints(save, character.id);
    progression.textContent = unlocked ? `Level ${progress.level} · ${progress.next ? `${progress.current}/${progress.next} XP` : 'Maximum XP'} · ${unspent} unspent` : '';
    abilityName.textContent = character.ability.name;
    abilityDescription.textContent = character.ability.description;
    cooldown.textContent = `${character.ability.cooldown.toFixed(1)}s cooldown`;
    passiveName.textContent = character.passive.name;
    passiveDescription.textContent = character.passive.description;
    lockCopy.textContent = unlocked ? '' : characterUnlockLabel(character);
    lockCopy.hidden = unlocked;
    select.disabled = !unlocked || selected;
    select.textContent = selected ? 'Current Timewalker' : unlocked ? 'Select Timewalker' : 'Timewalker Locked';
    select.classList.toggle('selected', selected);
    previousLabel.textContent = neighboringName(-1);
    nextLabel.textContent = neighboringName(1);
    dotButtons.forEach((button, dotIndex) => {
      button.classList.toggle('active', dotIndex === index);
      button.setAttribute('aria-selected', String(dotIndex === index));
    });
  };
  const move = (amount: number) => {
    index = (index + amount + CHARACTERS.length) % CHARACTERS.length;
    renderCharacter();
  };
  previous.addEventListener('click', () => move(-1));
  next.addEventListener('click', () => move(1));
  select.addEventListener('click', () => {
    const character = CHARACTERS[index];
    if (!save.unlockedCharacters.includes(character.id) || save.selectedCharacter === character.id) return;
    save.selectedCharacter = character.id;
    onChanged();
    renderCharacter();
  });
  let swipeStart = 0;
  stage.addEventListener('pointerdown', (event) => { swipeStart = event.clientX; });
  stage.addEventListener('pointerup', (event) => {
    const distance = event.clientX - swipeStart;
    if (Math.abs(distance) > 70) move(distance > 0 ? -1 : 1);
  });

  const root = h('section', { class: 'character-carousel' + (compact ? ' run-character-carousel' : ''), 'aria-label': 'Choose your timewalker' },
    h('div', { class: 'character-heading' },
      h('div', null, h('div', { class: 'character-kicker' }, 'Chronofall roster'), h('h3', null, 'Choose your timewalker')),
      h('div', { class: 'character-roster-count' }, `${save.unlockedCharacters.length} / ${CHARACTERS.length} unlocked`),
    ),
    h('div', { class: 'character-carousel-main' },
      previous, previousLabel,
      stage,
      nextLabel, next,
      h('div', { class: 'character-copy' },
        eyebrow,
        name,
        tagline,
        progression,
        h('div', { class: 'character-ability' },
          h('div', { class: 'character-ability-icon', 'aria-hidden': 'true' }, '✦'),
          h('div', null, h('small', null, 'Signature ability'), abilityName, abilityDescription),
          cooldown,
        ),
        h('div', { class: 'character-ability character-passive' },
          h('div', { class: 'character-ability-icon', 'aria-hidden': 'true' }, '◈'),
          h('div', null, h('small', null, 'Passive'), passiveName, passiveDescription),
        ),
        lockCopy,
        select,
      ),
    ),
    dots,
    h('div', { class: 'character-help sub' }, 'Browse freely · selection changes only when confirmed'),
  );
  renderCharacter();
  return root;
}

export type HubTab = 'characters' | 'skills' | 'equipment' | 'unlocks' | 'anchors' | 'achievements' | 'pets' | 'observatory';

export interface RunSetupActions {
  back(): void;
  confirm(): void;
  changed(): void;
  edit(tab: 'equipment' | 'pets'): void;
}

export function runSetupScreen(save: SaveData, reg: ContentRegistry, characters: CharacterLibrary, options: RunSetupOptions, a: RunSetupActions): HTMLElement {
  const summary = h('div', { class: 'run-review-grid' });
  const beginLabel = h('span');
  const renderSummary = () => {
    clear(summary);
    const character = characterDef(save.selectedCharacter);
    const progression = characterLevelProgress(save.characters[character.id].xp);
    const unspent = skillPointsForLevel(progression.level) - spentSkillPoints(save, character.id);
    const items = equippedItems(save, reg);
    const itemFor = (slot: ItemSlot) => items.find((item) => item.slot === slot)?.name ?? (slot === 'weapon' ? 'Spore Launcher' : 'None');
    const pet = PETS.find((entry) => entry.id === save.pets.active);
    const fact = (label: string, value: string) => h('div', { class: 'run-review-fact' }, h('small', null, label), h('strong', null, value));
    summary.append(
      fact('Timewalker', character.name),
      fact('Character level', String(progression.level)),
      fact('Unspent skill points', String(unspent)),
      fact('Signature', character.ability.name),
      fact('Passive', character.passive.name),
      fact('Starting point', reg.eras[options.startEra]?.name ?? 'Primordial Soup'),
      fact('Difficulty', paradoxLabel(options.paradox)),
      fact('Mutators', paradoxDef(options.paradox).description),
      fact('Party', options.players === 1 ? 'Solo' : `${options.players} players`),
      fact('Weapon', itemFor('weapon')),
      fact('Armor', itemFor('armor')),
      fact('Relic', itemFor('relic')),
      fact('Companion', pet?.name ?? 'No pet'),
    );
    beginLabel.textContent = `Begin as ${character.name}`;
  };
  const carousel = characterCarousel(save, characters, () => { a.changed(); renderSummary(); }, true);
  renderSummary();

  const prompt = livePrompt(() => {
    const labels = currentInputPrompts();
    return [`${labels.confirm} · ${labels.back}`];
  }, { class: 'run-input-hints sub', 'aria-live': 'polite' });

  const root = h('div', { class: 'screen run-setup-screen', 'data-preserve-initial-scroll': true },
    h('div', { class: 'panel col run-setup-panel' },
      h('header', { class: 'run-setup-heading' },
        h('div', null, h('div', { class: 'character-kicker' }, 'Run preparation'), h('h2', null, 'Enter the rift')),
        h('button', { class: 'small', onClick: a.back }, 'Back'),
      ),
      carousel,
      h('section', { class: 'run-review', 'aria-label': 'Run loadout' },
        h('div', { class: 'run-review-heading' },
          h('div', null, h('div', { class: 'character-kicker' }, 'Ready check'), h('h3', null, 'Run loadout')),
          h('div', { class: 'row run-review-edits' },
            h('button', { class: 'small', onClick: () => a.edit('equipment') }, 'Change Equipment'),
            h('button', { class: 'small', onClick: () => a.edit('pets') }, 'Change Pet'),
          ),
        ),
        summary,
      ),
      h('div', { class: 'run-setup-actions' },
        h('button', { class: 'small', onClick: a.back }, 'Back to menu'),
        h('button', { class: 'primary run-confirm', 'data-default-focus': true, onClick: a.confirm }, beginLabel, h('b', { 'aria-hidden': 'true' }, '→')),
      ),
      prompt,
    ),
  );
  return root;
}

export interface HubActions {
  back(): void;
  changed(): void;
  enterObservatory(): void;
}

function itemStatSummary(item: ItemDef): string {
  const mods = [...item.mods, ...(item.weapon?.ruleMods ?? [])];
  const labels = mods.slice(0, 4).map((mod) => `${mod.stat} ${mod.add !== undefined ? `${mod.add >= 0 ? '+' : ''}${mod.add}` : `×${mod.mul}`}`);
  if (item.weapon) labels.unshift(`${item.weapon.shape} · ${item.weapon.count} shot${item.weapon.count === 1 ? '' : 's'}`);
  return labels.join(' · ') || 'No stat modifiers';
}

function itemMetrics(item: ItemDef | null): Map<string, string> {
  const metrics = new Map<string, string>();
  if (!item) return metrics;
  if (item.weapon) {
    const weapon = item.weapon;
    metrics.set('Shots', String(weapon.count));
    metrics.set('Damage / shot', (weapon.bullet.damage * weapon.count).toFixed(2).replace(/\.00$/, ''));
    metrics.set('Fire interval', `${weapon.interval.toFixed(2)}s`);
    metrics.set('Projectile reach', (weapon.bullet.speed * weapon.bullet.lifetime).toFixed(1));
  }
  const mods = [...item.mods, ...(item.weapon?.ruleMods ?? [])];
  for (const mod of mods) {
    const value = mod.add !== undefined ? `${mod.add >= 0 ? '+' : ''}${mod.add}` : `×${mod.mul}`;
    metrics.set(mod.stat, metrics.has(mod.stat) ? `${metrics.get(mod.stat)}, ${value}` : value);
  }
  return metrics;
}

function equipmentComparison(selected: ItemDef | null, equipped: ItemDef | null): HTMLElement {
  const selectedMetrics = itemMetrics(selected);
  const equippedMetrics = itemMetrics(equipped);
  const labels = [...new Set([...selectedMetrics.keys(), ...equippedMetrics.keys()])];
  return h('div', { class: 'equipment-comparison' },
    h('div', { class: 'equipment-compare-head' }, h('span', null, 'Stat'), h('span', null, `Equipped · ${equipped?.name ?? 'None'}`), h('span', null, `Selected · ${selected?.name ?? 'None'}`)),
    ...labels.map((label) => h('div', { class: 'equipment-compare-row' }, h('span', null, label.replaceAll(/([A-Z])/g, ' $1')), h('span', null, equippedMetrics.get(label) ?? '—'), h('strong', null, selectedMetrics.get(label) ?? '—'))),
  );
}

export function hubScreen(save: SaveData, reg: ContentRegistry, characters: CharacterLibrary, models: ModelLibrary, a: HubActions, initialTab: HubTab = 'characters'): HTMLElement {
  const t = (key: Parameters<typeof tr>[1]) => tr(save.settings.locale, key);
  const shards = h('span', { class: 'shards' });
  const content = h('div');
  let tab: HubTab = initialTab;
  const equipmentSelection: Partial<Record<ItemSlot, string | null>> = {};
  const refresh = () => {
    shards.textContent = `◆ ${save.chronoshards} Chronoshards`;
    clear(content);
    if (tab === 'characters') {
      content.appendChild(characterCarousel(save, characters, a.changed));
    } else if (tab === 'skills') {
      const character = characterDef(save.selectedCharacter).id;
      const progress = characterLevelProgress(save.characters[character].xp);
      const spent = spentSkillPoints(save, character);
      const total = skillPointsForLevel(progress.level);
      content.append(h('div', { class: 'row', style: 'justify-content:space-between' }, h('div', null, h('h3', null, `${characterDef(character).name} · Level ${progress.level}`), h('div', { class: 'sub' }, progress.next ? `${progress.current} / ${progress.next} XP · ${total - spent} skill points available` : `Maximum level · ${total - spent} skill points available`)), h('button', { class: 'small', disabled: spent === 0, onClick: () => { respecSkills(save, character); a.changed(); refresh(); } }, 'Free respec')));
      const grid = h('div', { class: 'skill-grid' });
      for (const branch of ['offense', 'survival', 'signature'] as SkillBranch[]) {
        const column = h('section', { class: `skill-branch skill-${branch}` }, h('h3', null, branch));
        for (const node of SKILL_TREES[character].filter((entry) => entry.branch === branch)) {
          const rank = save.characters[character].allocated[node.id] ?? 0;
          const available = canAllocateSkill(character, save.characters[character].allocated, node.id, total);
          const gate = node.requires?.pointsInBranch ? `${node.requires.pointsInBranch} branch points required` : 'Root node';
          column.appendChild(h('div', { class: `card skill-node${rank ? ' selected' : available ? ' available' : ' locked'}` },
            h('div', { class: 'rarity' }, `${node.flag ? 'keystone' : branch} · ${rank}/${node.maxRanks}`),
            h('div', { class: 'name' }, node.name),
            h('div', { class: 'desc' }, node.desc),
            h('div', { class: 'skill-gate' }, `${gate} · ${node.cost} point${node.cost === 1 ? '' : 's'}/rank`),
            h('button', { class: 'small', disabled: !available, onClick: (e) => { e.stopPropagation(); if (allocateSkill(save, character, node.id)) { a.changed(); refresh(); } } }, rank >= node.maxRanks ? 'Maxed' : available ? `Spend ${node.cost}` : 'Locked')));
        }
        grid.appendChild(column);
      }
      content.appendChild(grid);
    } else if (tab === 'equipment') {
      content.appendChild(h('div', { class: 'sub' }, 'Inventory & loadout · inspect each item in 3D, compare it with the current slot, then equip deliberately. Drag previews to rotate.'));
      for (const slot of ['weapon', 'armor', 'relic'] as ItemSlot[]) {
        const owned = reg.itemList.filter((i) => i.slot === slot && save.inventory.includes(i.id));
        const equippedId = save.equipment[slot];
        if (!(slot in equipmentSelection)) equipmentSelection[slot] = equippedId ?? owned[0]?.id ?? null;
        const selected = reg.items.get(equipmentSelection[slot] ?? '') ?? null;
        const equipped = reg.items.get(equippedId ?? '') ?? null;
        const canvas = selected ? h('canvas', { class: 'item-preview-canvas', 'aria-label': `${selected.name} 3D preview` }) : null;
        if (canvas && selected) new ItemPreview(canvas, models).setItem(selected);
        const equipAction = () => {
          if (slot === 'weapon' && !selected) return;
          if (equip(save, reg, selected?.id ?? null, slot)) { a.changed(); refresh(); }
        };
        content.appendChild(h('section', { class: 'equipment-slot' },
          h('div', { class: 'equipment-slot-title' }, h('h3', null, slot), h('span', { class: 'sub' }, equipped ? `Equipped · ${equipped.name}` : 'Slot empty')),
          h('div', { class: 'equipment-showroom' },
            h('div', { class: 'item-preview-stage' }, canvas ?? h('div', { class: 'item-preview-empty' }, 'EMPTY SLOT')),
            h('div', { class: 'equipment-selected' },
              h('div', { class: 'rarity', style: selected ? `color:${RARITY_COLOR[selected.rarity]}` : '' }, selected ? `${selected.rarity} · era ${selected.era + 1}` : 'No item selected'),
              h('div', { class: 'name' }, selected?.name ?? '— none —'),
              h('div', { class: 'desc' }, selected?.desc ?? 'Unequip this optional slot.'),
              selected?.mechanic ? h('div', { class: 'equipment-mechanic' }, h('strong', null, selected.mechanic.name), h('span', null, selected.mechanic.description)) : null,
              h('button', { class: 'small primary', disabled: (selected?.id ?? null) === equippedId || (slot === 'weapon' && !selected), onClick: equipAction }, (selected?.id ?? null) === equippedId ? 'Equipped' : selected ? 'Equip selected' : 'Unequip slot')),
            equipmentComparison(selected, equipped),
          ),
        ));
        const grid = h('div', { class: 'grid equipment-inventory-grid' });
        if (slot !== 'weapon') grid.appendChild(h('button', { class: 'card inventory-card inventory-none' + (selected === null ? ' selected' : ''), onClick: () => { equipmentSelection[slot] = null; refresh(); } }, h('div', { class: 'name' }, '— none —'), h('div', { class: 'desc' }, 'Leave this equipment slot empty.')));
        for (const it of owned) {
          const isEquipped = save.equipment[slot] === it.id;
          const inspecting = selected?.id === it.id;
          grid.appendChild(h('button', { class: 'card inventory-card' + (inspecting ? ' selected' : ''), onClick: () => { equipmentSelection[slot] = it.id; refresh(); } },
            h('div', { class: `inventory-preview inventory-preview-${slot}`, style: `--item-color:#${(it.visual?.color ?? 0x62e5ff).toString(16).padStart(6, '0')}`, 'aria-hidden': 'true' }, slot === 'weapon' ? '⟡' : slot === 'armor' ? '◈' : '◇'),
            h('div', { class: 'rarity', style: `color:${RARITY_COLOR[it.rarity]}` }, `${it.rarity} · era ${it.era + 1}${isEquipped ? ' · EQUIPPED' : ''}${inspecting ? ' · INSPECTING' : ''}`),
            h('div', { class: 'name' }, it.name),
            h('div', { class: 'desc' }, it.desc),
            h('div', { class: 'item-stats' }, itemStatSummary(it))));
        }
        content.appendChild(grid);
      }
    } else if (tab === 'unlocks') {
      const list = h('div', { class: 'list' });
      for (const u of reg.unlocks) {
        const owned = save.unlocks.includes(u.id);
        list.appendChild(
          h(
            'div',
            { class: 'item' + (owned ? ' owned' : '') },
            h('div', null, h('div', null, u.name), h('div', { class: 'sub' }, u.desc)),
            owned ? h('span', { class: 'sub' }, 'Owned') : h('button', { class: 'small', disabled: save.chronoshards < u.cost, onClick: () => { if (buyUnlock(save, reg, u.id)) { a.changed(); refresh(); } } }, `◆ ${u.cost}`),
          ),
        );
      }
      content.appendChild(list);
    } else if (tab === 'anchors') {
      const list = h('div', { class: 'list' });
      for (const era of reg.eras) {
        const anchored = save.anchors.includes(era.index);
        list.appendChild(h('div', { class: 'item' + (anchored ? ' owned' : '') }, h('div', null, h('div', null, `${era.index + 1}. ${era.name}`), h('div', { class: 'sub' }, era.subtitle + (era.implemented ? '' : ' · (coming soon)'))), h('span', { class: 'sub' }, anchored ? 'Anchor unlocked' : 'Defeat the boss to anchor')));
      }
      content.appendChild(list);
    } else if (tab === 'pets') {
      const assignments = h('div', { class: 'pet-assignments' },
        h('div', { class: 'pet-assignment-heading' }, h('div', null, h('h3', null, 'Observatory assignments'), h('div', { class: 'sub' }, 'Assigned companions remain at home and provide their listed bonus. Active run companions cannot be assigned.'))),
        ...PET_ASSIGNMENT_STATIONS.map((station) => {
          const current = save.pets.assignments[station.id];
          const select = h('select', { class: 'pet-assignment-select', 'aria-label': `Assign pet to ${station.name}`, onChange: (event) => {
            const id = (event.currentTarget as HTMLSelectElement).value || null;
            if (assignPet(save, station.id, id)) { a.changed(); refresh(); }
          } },
          h('option', { value: '', selected: !current }, 'Unassigned'),
          ...save.pets.owned.filter((id) => id !== save.pets.active).map((id) => h('option', { value: id, selected: current === id }, PET_BY_ID.get(id)?.name ?? id)));
          const pet = current ? PET_BY_ID.get(current) : null;
          return h('div', { class: 'pet-assignment-slot' }, h('div', null, h('strong', null, station.name), h('span', null, pet ? `${pet.houseBonus.name} · ${pet.houseBonus.description}` : 'Choose an owned companion')), select);
        }),
      );
      content.appendChild(assignments);
      const grid = h('div', { class: 'grid' });
      grid.appendChild(h('div', { class: 'card' + (save.pets.active === null ? ' selected' : ''), onClick: () => { selectPet(save, null); a.changed(); refresh(); } }, h('div', { class: 'rarity' }, 'Companion slot'), h('div', { class: 'name' }, 'No pet'), h('div', { class: 'desc' }, 'Run without an autonomous companion.')));
      for (const pet of PETS) {
        const owned = save.pets.owned.includes(pet.id);
        const active = save.pets.active === pet.id;
        const level = petLevel(save.pets.xp[pet.id] ?? 0);
        const assigned = PET_ASSIGNMENT_STATIONS.find((station) => save.pets.assignments[station.id] === pet.id);
        const available = canAcquirePet(save, pet.id);
        grid.appendChild(h('div', { class: 'card pet-card' + (active ? ' selected' : owned ? '' : ' locked') },
          h('div', { class: 'rarity' }, active ? `Active · Level ${level}` : assigned ? `${assigned.name} · Level ${level}` : owned ? `Owned · Level ${level}` : petAcquisitionLabel(save, pet.id)),
          h('div', { class: 'name' }, pet.name),
          h('div', { class: 'desc' }, `${pet.description} Leveling increases damage and fire rate.`),
          h('div', { class: 'pet-identity' }, h('strong', null, `${pet.role} · ${pet.ability.name}`), h('span', null, pet.ability.description), h('strong', null, pet.passive.name), h('span', null, pet.passive.description), h('strong', null, `Home · ${pet.houseBonus.name}`), h('span', null, pet.houseBonus.description)),
          owned
            ? h('button', { class: 'small', disabled: active, onClick: () => { if (selectPet(save, pet.id)) { a.changed(); refresh(); } } }, active ? 'Active companion' : assigned ? 'Recall & activate' : 'Set active')
            : h('button', { class: 'small primary', disabled: !available, onClick: () => { if (buyPet(save, pet.id)) { a.changed(); refresh(); } } }, available ? (pet.cost > 0 ? `Adopt · ◆ ${pet.cost}` : 'Claim companion') : petAcquisitionLabel(save, pet.id))));
      }
      content.appendChild(grid);
    } else if (tab === 'observatory') {
      a.enterObservatory();
    } else {
      const categories: AchievementCategory[] = ['journey', 'character', 'objective', 'paradox', 'challenge'];
      for (const category of categories) {
        const achievements = ACHIEVEMENTS.filter((achievement) => achievement.category === category);
        const completed = achievements.filter((achievement) => save.achievements.includes(achievement.id)).length;
        const grid = h('div', { class: 'grid achievement-grid' });
        for (const achievement of achievements) {
          const progress = Math.min(achievement.target, save.achievementProgress[achievement.id] ?? achievement.progress(save));
          const unlocked = save.achievements.includes(achievement.id);
          const percent = Math.round(progress / achievement.target * 100);
          grid.appendChild(h('div', { class: 'card achievement-card' + (unlocked ? ' selected' : ' locked') },
            h('div', { class: 'rarity' }, unlocked ? 'Unlocked' : `${progress} / ${achievement.target}`),
            h('div', { class: 'name' }, achievement.name),
            h('div', { class: 'desc' }, achievement.description),
            h('div', { class: 'achievement-progress', role: 'progressbar', 'aria-valuemin': '0', 'aria-valuemax': achievement.target, 'aria-valuenow': progress }, h('span', { style: `width:${percent}%` })),
            h('div', { class: 'achievement-reward' }, `Reward · ${achievementRewardLabel(achievement)}`)));
        }
        content.append(h('section', { class: 'achievement-section' }, h('div', { class: 'achievement-heading' }, h('h3', null, ACHIEVEMENT_CATEGORY_LABELS[category]), h('span', null, `${completed} / ${achievements.length}`)), grid));
      }
    }
  };
  const tabBtn = (id: typeof tab, label: string) => h('button', { class: 'small' + (tab === id ? ' active' : ''), onClick: (e) => { tab = id; (e.currentTarget as HTMLElement).parentElement?.querySelectorAll('button').forEach((b) => b.classList.remove('active')); (e.currentTarget as HTMLElement).classList.add('active'); refresh(); } }, label);
  refresh();
  return h(
    'div',
    { class: 'screen hub-screen' },
    h(
      'div',
      { class: 'panel col timeline-panel' },
      h('div', { class: 'row', style: 'justify-content:space-between' }, h('h2', null, t('hub.title')), shards),
      h('div', { class: 'sub' }, `Runs ${save.stats.runs} · Wins ${save.stats.wins} · Best score ${save.stats.bestScore} · Rerolls ${countUnlocks(save, reg, 'reroll')} · Banishes ${countUnlocks(save, reg, 'banish')}`),
      h('div', { class: 'tabs' }, tabBtn('characters', t('hub.characters')), tabBtn('skills', t('hub.skills')), tabBtn('equipment', t('hub.inventory')), tabBtn('unlocks', t('hub.unlocks')), tabBtn('pets', t('hub.pets')), tabBtn('observatory', t('hub.observatory')), tabBtn('anchors', t('hub.anchors')), tabBtn('achievements', t('hub.achievements'))),
      content,
      h('div', { class: 'row', style: 'margin-top:12px' }, h('button', { class: 'primary', onClick: () => a.back() }, t('common.back'))),
    ),
  );
}

export function observatoryHud(onPause: () => void, onBuild?: () => void): HTMLElement {
  return h('div', { class: 'observatory-game-ui', 'aria-label': 'The Observatory' },
    h('div', { class: 'observatory-game-title' }, h('strong', null, 'THE OBSERVATORY'), h('span', null, 'House & yard')),
    h('div', { class: 'observatory-game-prompt', 'data-observatory-prompt': true, 'aria-live': 'polite' }, 'Explore your home'),
    h('div', { class: 'observatory-game-help' }, actionPrompt('move', 'Move'), actionPrompt('ability', 'Ability'), h('span', null, 'E / X · Interact')),
    onBuild ? h('button', { class: 'small observatory-build', onClick: onBuild }, 'Build & Decorate') : null,
    h('button', { class: 'small observatory-pause', onClick: onPause, 'aria-label': 'Pause Observatory' }, 'II'),
  );
}

export function observatoryPlacementHud(name: string, valid: boolean, onCancel: () => void): HTMLElement {
  return h('div', { class: 'observatory-game-ui observatory-building', 'aria-label': 'Observatory placement mode' },
    h('div', { class: 'observatory-game-title' }, h('strong', null, 'PLACEMENT MODE'), h('span', null, name)),
    h('div', { class: `observatory-game-prompt ${valid ? 'valid' : 'invalid'}`, 'data-observatory-prompt': true, 'aria-live': 'polite' }, valid ? 'Ready to place' : 'Move away from stations, walls, furniture, and the player'),
    h('div', { class: 'observatory-game-help' }, actionPrompt('move', 'Move'), h('span', null, 'Ability · Rotate 90°'), h('span', null, 'E / X · Place')),
    h('button', { class: 'small observatory-build', onClick: onCancel }, 'Cancel'),
  );
}

export function observatoryBuildScreen(save: SaveData, a: { place(moduleId: string): void; buy(moduleId: string): void; move(id: string): void; remove(id: string): void; back(): void }): HTMLElement {
  const grid = h('div', { class: 'grid observatory-module-grid' });
  for (const module of HOUSE_MODULES) {
    const owned = save.house.modules[module.id] ?? 0;
    const placed = save.house.placements.filter((placement) => placement.moduleId === module.id).length;
    const available = owned - placed;
    grid.append(h('div', { class: 'card observatory-module-card' },
      h('div', { class: 'rarity' }, `${module.zone} · ${module.width} × ${module.depth}`),
      h('div', { class: 'name' }, module.name),
      h('div', { class: 'desc' }, module.description),
      h('div', { class: 'sub' }, `Owned ${owned} · placed ${placed}`),
      available > 0
        ? h('button', { class: 'small primary', onClick: () => a.place(module.id) }, 'Place owned')
        : h('button', { class: 'small primary', disabled: save.chronoshards < module.cost, onClick: () => a.buy(module.id) }, `Buy & place · ◆ ${module.cost}`)));
  }
  const placed = h('div', { class: 'observatory-placed-list' });
  for (const placement of save.house.placements) {
    const module = HOUSE_MODULES.find((entry) => entry.id === placement.moduleId);
    if (!module) continue;
    placed.append(h('div', { class: 'item row' }, h('span', null, module.name), h('span', { class: 'sub' }, `${placement.x.toFixed(1)}, ${placement.z.toFixed(1)}`), h('button', { class: 'small', onClick: () => a.move(placement.id) }, 'Move'), h('button', { class: 'small danger', onClick: () => a.remove(placement.id) }, 'Remove')));
  }
  if (!save.house.placements.length) placed.append(h('div', { class: 'sub' }, 'No custom modules placed yet.'));
  return h('div', { class: 'screen observatory-overlay' }, h('div', { class: 'panel col observatory-builder-panel' },
    h('div', { class: 'character-kicker' }, 'Modular construction'),
    h('div', { class: 'row', style: 'justify-content:space-between' }, h('h2', null, 'Build & Decorate'), h('div', { class: 'shards' }, `◆ ${save.chronoshards}`)),
    h('div', { class: 'sub' }, 'Place on a half-unit grid and rotate in 90° steps. Placement data stores full angles so finer controls can be added later.'),
    grid,
    h('h3', null, 'Placed modules'), placed,
    h('button', { class: 'small', onClick: a.back }, 'Back to Observatory'),
  ));
}

export function observatoryPauseScreen(a: { resume(): void; character(): void; menu(): void }): HTMLElement {
  return h('div', { class: 'screen observatory-overlay' }, h('div', { class: 'panel col observatory-panel' },
    h('div', { class: 'character-kicker' }, 'House paused'),
    h('h2', null, 'The Observatory'),
    h('div', { class: 'sub' }, 'Your home remains exactly as you left it.'),
    h('button', { class: 'primary', 'data-default-focus': true, onClick: a.resume }, 'Resume'),
    h('button', { onClick: a.character }, 'Character / Inventory'),
    h('button', { class: 'small', onClick: a.menu }, 'Return to Main Menu'),
  ));
}

export function observatoryUpgradeScreen(save: SaveData, id: Extract<ObservatoryStationId, 'chronolab' | 'garden'>, a: { upgrade(): void; back(): void }): HTMLElement {
  const tier = save.house.rooms[id] ?? 0;
  const name = id === 'chronolab' ? 'Chronolab' : 'Temporal Garden';
  const base = id === 'chronolab' ? 150 : 120;
  const cost = base * (tier + 1);
  const benefit = id === 'chronolab' ? 'starting reroll' : 'run-start healing pickup';
  return h('div', { class: 'screen observatory-overlay' }, h('div', { class: 'panel col observatory-panel' },
    h('div', { class: 'character-kicker' }, 'Observatory station'),
    h('h2', null, name),
    h('div', { class: 'sub' }, `Tier ${tier} / 3 · Each tier grants one ${benefit}.`),
    h('div', { class: 'shards' }, `◆ ${save.chronoshards} Chronoshards`),
    tier >= 3
      ? h('div', { class: 'item owned' }, h('strong', null, 'Fully upgraded'))
      : h('button', { class: 'primary', disabled: save.chronoshards < cost, onClick: a.upgrade }, `Upgrade to tier ${tier + 1} · ◆ ${cost}`),
    h('button', { class: 'small', onClick: a.back }, 'Back to Observatory'),
  ));
}

export function levelUpScreen(sim: Sim, onPick: (id: string) => void, onReroll: () => void, onBanish: (id: string) => void, forPlayer?: number, playerName?: string): HTMLElement {
  // Co-op: XP is shared but picks are per player. Local co-op shows whichever
  // player owes one first (Sim.pickingPlayer); online passes the local player.
  const player = forPlayer ?? Math.max(0, sim.pickingPlayer());
  const cards = h('div', { class: 'row', style: 'justify-content:center' });
  const render = () => {
    clear(cards);
    const owned = sim.upgradesFor(player);
    for (const u of sim.offersFor(player)) {
      cards.appendChild(upgradeCard(u, owned.get(u.id) ?? 0, () => onPick(u.id), sim.run.banishesLeft[player] > 0 ? () => { onBanish(u.id); render(); } : null));
    }
    rerollBtn.disabled = sim.run.rerollsLeft[player] <= 0;
    rerollBtn.textContent = `Reroll (${sim.run.rerollsLeft[player]})`;
  };
  const rerollBtn = h('button', { onClick: () => { onReroll(); render(); } }, 'Reroll');
  render();
  const whose = sim.config.playerCount > 1 ? ` · ${playerName ?? `Player ${player + 1}`}` : '';
  return h('div', { class: 'screen' }, h('h2', null, `Level ${sim.run.level}${whose}`), h('div', { class: 'sub' }, 'Choose an upgrade'), cards, h('div', { class: 'row' }, rerollBtn));
}

function upgradeCard(u: UpgradeDef, stacks: number, onPick: () => void, onBanish: (() => void) | null): HTMLElement {
  return h(
    'div',
    { class: 'card', onClick: onPick, style: `border-color:${RARITY_COLOR[u.rarity]}55` },
    h('div', { class: 'rarity', style: `color:${RARITY_COLOR[u.rarity]}` }, u.rarity + (stacks > 0 ? ` · owned ${stacks}/${u.maxStacks}` : '')),
    h('div', { class: 'name' }, u.name),
    h('div', { class: 'desc' }, u.desc),
    onBanish ? h('button', { class: 'small', style: 'align-self:flex-end;margin-top:6px', onClick: (e) => { e.stopPropagation(); onBanish(); } }, 'Banish') : null,
  );
}

export type PauseSettings = SettingsData;

export interface PauseActions {
  resume(): void;
  quit(): void;
  setQuality(q: 'low' | 'med' | 'high'): void;
  setMusicVolume(v: number): void;
  setSfxVolume(v: number): void;
  setCameraDistance(v: number): void;
  setColorblindMode(mode: ColorblindMode): void;
  setControlsHud(mode: ControlsHudMode): void;
  toggle(key: 'screenShake' | 'showDamageNumbers' | 'highContrastProjectiles' | 'reducedFlash', on: boolean): void;
  controls: ControlsActions;
}

const pct = (v: number) => `${Math.round(v * 100)}%`;
const fx = (v: number, d = 1) => (Math.round(v * 10 ** d) / 10 ** d).toString();

/** Stat sheet: resolved stats + estimated DPS + fire-rule modifiers. Pure view over Stats. */
export function statSheet(s: Stats, weaponInterval: number, characterId: CharacterId = 'knight'): HTMLElement {
  const character = characterDef(characterId);
  const shotsPerSec = s.fireRate / weaponInterval;
  const avgCrit = 1 + s.critChance * (s.critMult - 1);
  const echo = 1 + s.doubleTap * s.doubleTapDamage;
  const dps = s.damage * s.damageMult * (1 + s.projectiles) * shotsPerSec * avgCrit * echo;
  const rows: [string, string][] = [
    ['Damage', `${fx(s.damage)} × ${fx(s.damageMult, 2)}`],
    ['Fire rate', `${fx(shotsPerSec, 2)}/s`],
    ['Est. DPS (standing)', fx(dps, 0)],
    ['Crit', `${pct(s.critChance)} · ×${fx(s.critMult, 2)}`],
    ['Projectiles', `${1 + s.projectiles}  pierce ${s.pierce}  bounce ${s.bounce}`],
    ['Proj. speed / size', `×${fx(s.projectileSpeed, 2)} / ×${fx(s.projectileSize, 2)}`],
    ['Range', fx(s.range)],
    ['Max HP', `${Math.round(s.maxHp)}  armor ${fx(s.armor)}`],
    ['Regen / lifesteal', `${fx(s.regen)}/s · ${pct(s.lifesteal)}`],
    ['Move speed', fx(s.moveSpeed)],
    [`Passive · ${character.passive.name}`, character.passive.description],
    [character.ability.name, character.id === 'knight' ? `${s.dashCharges}× · ${fx(s.dashCooldown)}s cd · ${fx(s.dashDistance)}u` : `${character.ability.charges}× · ${fx(character.ability.cooldown)}s cd`],
    ['Pickup radius', fx(s.pickupRadius)],
    ['XP / shards', `×${fx(s.xpMult, 2)} / ×${fx(s.shardMult, 2)}`],
  ];
  const rule: [string, string][] = [];
  if (s.doubleTap > 0) rule.push(['Double Tap', `${s.doubleTap} echo${s.doubleTap > 1 ? 'es' : ''} × ${pct(s.doubleTapDamage)} after ${fx(s.doubleTapDelay, 2)}s`]);
  if (s.movingFireMult > 0) rule.push(['Fire while moving', pct(s.movingFireMult)]);
  if (s.dashFireMult > 0) rule.push(['Fire while dashing', pct(s.dashFireMult)]);
  if (s.stillBonusWindow > 0) rule.push(['First-strike window', `${fx(s.stillBonusWindow)}s × ${fx(s.stillBonusMult, 2)}`]);
  if (s.stillRamp > 0) rule.push(['Entrench', `+${pct(s.stillRamp)}/s up to +${pct(s.stillRampMax)}`]);
  const el: [string, string][] = [];
  if (s.fireChance > 0) el.push(['Fire', `${pct(s.fireChance)} · ${fx(s.fireDps)}/s`]);
  if (s.frostChance > 0) el.push(['Frost', `${pct(s.frostChance)} · −${pct(s.frostSlow)} speed`]);
  if (s.lightningChance > 0) el.push(['Lightning', `${pct(s.lightningChance)} · ${s.lightningChains} chains`]);
  if (s.temporalChance > 0) el.push(['Temporal', `${pct(s.temporalChance)} · −${pct(s.temporalSlow)} speed`]);
  if (s.temporalRewind > 0) el.push(['Rewind pulse', `${fx(s.temporalRewind)}u every ${fx(s.temporalPulseInterval)}s`]);
  const table = (title: string, r: [string, string][]) => (r.length ? [h('h3', null, title), h('table', { class: 'stats' }, ...r.map(([k, v]) => row(k, v)))] : []);
  return h('div', { class: 'col', style: 'min-width:260px' }, ...table('Stats', rows), ...table('Fire rule', rule), ...table('Elements', el));
}

/** Badges for every upgrade taken this run (icon + stacks, rarity-coloured). */
export function upgradeBadges(sim: Sim, player = 0): HTMLElement {
  const wrap = h('div', { class: 'badges' });
  const entries = [...sim.upgradesFor(player).entries()].map(([id, n]) => [sim.reg.upgrades.get(id), n] as const).filter((e) => e[0]);
  if (!entries.length) wrap.appendChild(h('div', { class: 'sub' }, 'No upgrades yet.'));
  for (const [u, n] of entries) {
    wrap.appendChild(
      h(
        'div',
        { class: 'badge', style: `--rc:${RARITY_COLOR[u!.rarity]}`, title: `${u!.name} — ${u!.desc}` },
        h('span', { class: 'glyph' }, upgradeIcon(u!)),
        h('span', { class: 'bname' }, u!.name),
        n > 1 ? h('span', { class: 'stack' }, `×${n}`) : null,
      ),
    );
  }
  return wrap;
}

/**
 * Pause screen. In co-op (2+ players) a tab row switches the stat sheet,
 * upgrades and equipment between players; `view.player` picks the opening tab
 * (online: the local player) and `view.names` labels the tabs. Solo is unchanged.
 */
export function pauseScreen(sim: Sim, settings: PauseSettings, a: PauseActions, view: { player?: number; names?: string[]; online?: boolean } = {}): HTMLElement {
  const toggle = (label: string, key: 'screenShake' | 'showDamageNumbers' | 'highContrastProjectiles' | 'reducedFlash') =>
    h('label', { class: 'row', style: 'gap:6px;cursor:pointer' }, h('input', { type: 'checkbox', checked: settings[key], onChange: (e) => a.toggle(key, (e.target as HTMLInputElement).checked) }), h('span', { class: 'sub' }, label));
  const count = sim.config.playerCount;
  let shown = Math.max(0, Math.min(count - 1, view.player ?? 0));
  const label = (i: number) => view.names?.[i] ?? `Player ${i + 1}`;
  const details = h('div', { class: 'row', style: 'align-items:flex-start;gap:28px' });
  const tabs = h('div', { class: 'row pause-player-tabs', role: 'tablist' });
  const render = () => {
    clear(details);
    const equipped = sim.config.equipment[shown] ?? [];
    details.append(
      statSheet(sim.getStats(shown), sim.ctx.weapons[shown].interval, sim.ctx.characters[shown]),
      h(
        'div',
        { class: 'col', style: 'flex:1;min-width:260px' },
        h('h3', null, `Upgrades (${[...sim.upgradesFor(shown).values()].reduce((x, y) => x + y, 0)})`),
        upgradeBadges(sim, shown),
        h('h3', null, 'Equipment'),
        h('div', { class: 'badges' }, ...equipped.map((it) => h('div', { class: 'badge', style: `--rc:${RARITY_COLOR[it.rarity]}`, title: it.desc }, h('span', { class: 'glyph' }, it.slot === 'weapon' ? '⌖' : it.slot === 'armor' ? '⛨' : '❂'), h('span', { class: 'bname' }, it.name)))),
      ),
    );
    clear(tabs);
    for (let i = 0; i < count; i++) {
      tabs.appendChild(h('button', { class: i === shown ? 'small primary' : 'small', role: 'tab', 'aria-selected': i === shown ? 'true' : 'false', onClick: () => { shown = i; render(); } }, `${label(i)} · ${characterDef(sim.ctx.characters[i]).name}`));
    }
  };
  render();
  return h(
    'div',
    { class: 'screen' },
    h(
      'div',
      { class: 'panel col', style: 'min-width:min(94vw,860px)' },
      h('div', { class: 'row', style: 'justify-content:space-between' }, h('h2', null, 'Paused'), h('span', { class: 'sub' }, `${sim.currentEra.name} · ${sim.currentStage.name} · Level ${sim.run.level} · ${fmtTime(sim.run.elapsed)}`)),
      view.online ? h('div', { class: 'sub', style: 'color:var(--gold)' }, 'Online run — the game keeps going for your party while this menu is open.') : null,
      count > 1 ? tabs : null,
      details,
      h('h3', null, 'Settings'),
      h(
        'div',
        { class: 'row' },
        h('span', { class: 'sub' }, 'Quality'),
        ...(['low', 'med', 'high'] as const).map((q) => h('button', { class: settings.quality === q ? 'small primary' : 'small', onClick: () => a.setQuality(q) }, q)),
        h('span', { class: 'sub', style: 'margin-left:12px' }, 'Music'),
        h('input', { type: 'range', min: 0, max: 1, step: 0.05, value: settings.musicVolume, onInput: (e) => a.setMusicVolume(Number((e.target as HTMLInputElement).value)) }),
        h('span', { class: 'sub', style: 'margin-left:12px' }, 'SFX'),
        h('input', { type: 'range', min: 0, max: 1, step: 0.05, value: settings.sfxVolume, onInput: (e) => a.setSfxVolume(Number((e.target as HTMLInputElement).value)) }),
        h('span', { class: 'sub', style: 'margin-left:12px' }, 'Camera'),
        h('input', { type: 'range', min: 26, max: 44, step: 1, value: settings.cameraDistance, onInput: (e) => a.setCameraDistance(Number((e.target as HTMLInputElement).value)) }),
        toggle('Screen shake', 'screenShake'),
        toggle('Damage numbers', 'showDamageNumbers'),
        toggle('High-contrast projectiles', 'highContrastProjectiles'),
        toggle('Reduced flash', 'reducedFlash'),
        h('select', { value: settings.colorblindMode, onChange: (e) => a.setColorblindMode((e.target as HTMLSelectElement).value as ColorblindMode) }, ...(['none', 'protanopia', 'deuteranopia', 'tritanopia'] as const).map((mode) => h('option', { value: mode }, mode === 'none' ? 'Default colors' : mode))),
        h('span', { class: 'sub' }, 'Controls HUD'),
        h('select', { onChange: (e) => a.setControlsHud((e.target as HTMLSelectElement).value as ControlsHudMode) }, h('option', { value: 'always', selected: settings.controlsHud === 'always' }, 'Always'), h('option', { value: 'firstRuns', selected: settings.controlsHud === 'firstRuns' }, 'First 3 runs'), h('option', { value: 'never', selected: settings.controlsHud === 'never' }, 'Never')),
      ),
      h('details', { class: 'pause-controls' }, h('summary', null, 'Controls'), controlsPanel(a.controls)),
      h('div', { class: 'row', style: 'margin-top:8px' }, h('button', { class: 'primary', onClick: a.resume }, 'Resume'), h('button', { onClick: a.quit }, 'Abandon run')),
    ),
  );
}

export function resultsScreen(result: RunResult, sim: Sim, onHub: () => void, onAgain: () => void, submissionStatus?: string, againLabel = 'Run again'): HTMLElement {
  return h(
    'div',
    { class: 'screen' },
    h(
      'div',
      { class: 'panel col', style: 'text-align:center;align-items:center' },
      h('h2', null, result.victory ? 'Timeline Restored' : 'Lost to Time'),
      h('div', { class: 'sub' }, `${sim.currentEra.name} · ${sim.currentStage.name} · v${GAME_VERSION}`),
      h('table', null,
        row('Score', result.score.toString()),
        row('Time survived', fmtTime(result.timeSurvivedSec)),
        row('Kills', result.kills.toString()),
        row('Level reached', result.level.toString()),
        row('Eras cleared', result.erasCleared.toString()),
        row('Bosses killed', result.bossesKilled.toString()),
        row('Chronoshards earned', `◆ ${result.shards}`),
        row('Character XP', `+${result.characterXp} · Level ${result.characterLevel}`),
        result.petXp ? row('Pet XP', `+${result.petXp}`) : null,
      ),
      result.newAnchors.length ? h('div', { style: 'color:var(--gold)' }, `New Temporal Anchor: ${result.newAnchors.map((e) => sim.reg.eras[e].name).join(', ')}`) : null,
      result.newCharacters.length ? h('div', { style: 'color:var(--accent);font-weight:700' }, `New timewalker unlocked: ${result.newCharacters.map((id) => characterDef(id).name).join(', ')}`) : null,
      result.newPets.length ? h('div', { style: 'color:var(--accent);font-weight:700' }, `New companion joined the Observatory: ${result.newPets.map((id) => PET_BY_ID.get(id)?.name ?? id).join(', ')}`) : null,
      result.newAchievements.length ? h('div', { style: 'color:var(--gold);font-weight:700' }, `Achievement unlocked: ${result.newAchievements.map((id) => ACHIEVEMENTS.find((a) => a.id === id)?.name ?? id).join(', ')}`) : null,
      submissionStatus ? h('div', { class: 'run-submit-status sub' }, submissionStatus) : null,
      result.drops.length
        ? h(
            'div',
            { class: 'col', style: 'align-items:center;gap:4px' },
            h('div', { class: 'sub' }, 'New equipment — equip it in The Timeline'),
            h(
              'div',
              { class: 'badges' },
              ...result.drops.map((id) => {
                const it = sim.reg.items.get(id);
                if (!it) return null;
                return h(
                  'div',
                  { class: 'badge', style: `--rc:${RARITY_COLOR[it.rarity]}`, title: `${it.name} — ${it.desc}` },
                  h('span', { class: 'glyph' }, it.slot === 'weapon' ? '⌖' : it.slot === 'armor' ? '⛨' : '❂'),
                  h('span', { class: 'bname' }, it.name),
                );
              }),
            ),
          )
        : null,
      h('div', { class: 'row' }, h('button', { class: 'primary', onClick: onAgain }, againLabel), h('button', { onClick: onHub }, 'The Timeline')),
    ),
  );
}

function row(k: string, v: string): HTMLElement {
  return h('tr', null, h('td', { class: 'sub' }, k), h('td', { style: 'text-align:right;font-weight:700' }, v));
}

export function leaderboardScreen(backend: CloudBackend, onBack: () => void, onWatch: (replay: import('../meta/replay').ReplayData) => void): HTMLElement {
  const body = h('div', { class: 'sub' }, 'Loading…');
  const context = h('div', { class: 'sub leaderboard-context' });
  const pageLabel = h('span', { class: 'sub' });
  let kind: LeaderboardKind = 'highestScore';
  let page = 0;
  const pageSize = 10;
  let requestId = 0;
  const prev = h('button', { class: 'small', onClick: () => { if (page > 0) { page--; void load(); } } }, 'Previous');
  const next = h('button', { class: 'small', onClick: () => { page++; void load(); } }, 'Next');
  const formatValue = (value: number) => kind === 'fastestClear' ? fmtTime(value) : String(value);
  const watchButton = (entry: import('../meta/backend').LeaderboardEntry) => entry.hasReplay ? h('button', { class: 'small', onClick: async () => {
    const replay = await backend.fetchReplay(kind, entry.uid, kind === 'daily' ? dailyKey() : undefined, entry.seed);
    if (replay) onWatch(replay);
  } }, 'Watch') : h('span', { class: 'sub' }, '—');
  const load = async () => {
    const id = ++requestId;
    body.textContent = 'Loading…';
    try {
      const result = await backend.fetchLeaderboard(kind, dailyKey(), page, pageSize);
      if (id !== requestId) return;
      clear(body);
      const currentUid = backend.currentUser()?.uid;
      if (!result.entries.length) body.textContent = page === 0 ? 'No entries yet.' : 'No entries on this page.';
      else body.appendChild(h('table', { class: 'leaderboard-table' }, ...result.entries.map((entry, index) => h('tr', { class: entry.uid === currentUid || entry.uid === 'local' ? 'own' : '' }, h('td', null, `#${page * pageSize + index + 1}`), h('td', null, entry.name), h('td', { style: 'text-align:right' }, formatValue(entry.value)), h('td', null, watchButton(entry))))));
      const ownOnPage = result.entries.some((entry) => entry.uid === currentUid || entry.uid === 'local');
      if (result.ownEntry && result.ownRank && !ownOnPage) body.appendChild(h('div', { class: 'leaderboard-own' }, h('strong', null, `Your best · #${result.ownRank}`), h('span', null, result.ownEntry.name), h('span', null, formatValue(result.ownEntry.value)), watchButton(result.ownEntry)));
      const pages = Math.max(1, Math.ceil(result.total / pageSize));
      pageLabel.textContent = `Page ${Math.min(page + 1, pages)} of ${pages} · ${result.total} ranked`;
      (prev as HTMLButtonElement).disabled = page <= 0;
      (next as HTMLButtonElement).disabled = page + 1 >= pages;
    } catch (e) {
      if (id !== requestId) return;
      body.textContent = 'Failed to load: ' + (e as Error).message;
    }
  };
  const updateContext = () => { context.textContent = kind === 'daily' ? `Daily resets in ${dailyCountdown()} UTC · first completed attempt scores` : 'Your best entry is highlighted.'; };
  const tabBtn = (k: LeaderboardKind, label: string) => h('button', { class: 'small' + (kind === k ? ' active' : ''), onClick: (event) => { kind = k; page = 0; updateContext(); (event.currentTarget as HTMLElement).parentElement?.querySelectorAll('button').forEach((button) => button.classList.remove('active')); (event.currentTarget as HTMLElement).classList.add('active'); void load(); } }, label);
  void load();
  const root = h(
    'div',
    { class: 'screen' },
    h(
      'div',
      { class: 'panel col', style: 'min-width:min(92vw,600px)' },
      h('h2', null, 'Leaderboards'),
      h('div', { class: 'tabs' }, tabBtn('highestScore', 'Score'), tabBtn('highestEra', 'Era'), tabBtn('fastestClear', 'Fastest clear'), tabBtn('daily', 'Daily')),
      context,
      body,
      h('div', { class: 'row leaderboard-pages' }, prev, pageLabel, next),
      h('div', { class: 'row', style: 'margin-top:12px' }, h('button', { class: 'primary', onClick: onBack }, 'Back')),
    ),
  );
  updateContext();
  const countdownTimer = window.setInterval(() => {
    if (!root.isConnected) return window.clearInterval(countdownTimer);
    updateContext();
  }, 1000);
  return root;
}

/** Zoom hint: wheel on keyboard/mouse (plus bound keys), triggers on a pad, pinch on touch. */
function zoomPromptParts(): (Node | string)[] {
  const device = activeDevice();
  if (device.kind === 'touch') return [h('span', { class: 'prompt-label' }, 'Pinch to zoom')];
  const parts = [...glyphsFor('zoomIn').map(glyphEl), ...glyphsFor('zoomOut').map(glyphEl)];
  if (device.kind === 'keyboard') parts.unshift(glyphEl({ text: 'Wheel', kind: 'key' }));
  return [...parts, h('span', { class: 'prompt-label' }, 'Zoom')];
}
